import { useEffect, useState, type ReactNode } from 'react';
import Button from '@components/ui/Button';
import Input from '@components/ui/Input';
import { ModalShell } from '../shared';

interface ConfirmStockOpsDialogProps {
    isOpen: boolean;
    storeName: string;
    title: string;
    body: ReactNode;
    confirmLabel: string;
    isLoading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
}

export default function ConfirmStockOpsDialog({
    isOpen,
    storeName,
    title,
    body,
    confirmLabel,
    isLoading,
    onCancel,
    onConfirm,
}: ConfirmStockOpsDialogProps) {
    const [typedName, setTypedName] = useState('');

    useEffect(() => {
        if (isOpen) {
            setTypedName('');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.stopImmediatePropagation();
            if (!isLoading) {
                onCancel();
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);
        return () => document.removeEventListener('keydown', handleKeyDown, true);
    }, [isOpen, isLoading, onCancel]);

    const nameMatches = typedName.trim() === storeName.trim();

    const handleClose = () => {
        if (!isLoading) {
            onCancel();
        }
    };

    const footer = (
        <>
            <Button variant="secondary" className="flex-1" onClick={onCancel} disabled={isLoading}>
                Cancel
            </Button>
            <Button
                variant="destructive"
                className="flex-1"
                isLoading={isLoading}
                disabled={!nameMatches}
                onClick={onConfirm}
            >
                {confirmLabel}
            </Button>
        </>
    );

    return (
        <ModalShell
            isOpen={isOpen}
            title={title}
            onClose={handleClose}
            footer={footer}
            maxWidth="sm"
            elevation="nested"
        >
            <div className="space-y-4">
                <div className="text-sm text-[#475569] space-y-2">{body}</div>
                <Input
                    label="Type the store name to confirm"
                    placeholder="Store name"
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    disabled={isLoading}
                    autoComplete="off"
                />
            </div>
        </ModalShell>
    );
}
